/* eslint-disable no-nested-ternary */
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { Loader, X } from 'react-feather';

import { useContactDetail } from '@/Users/hooks/useContactDetail';
import { useContactMutations } from '@/Users/hooks/useContactMutations';
import { ContactDetailModalProps } from '@/Users/types/userTypes';

const ContactDetailModal: React.FC<ContactDetailModalProps> = ({
  contactId,
  onClose,
  userUuid,
}) => {
  const { contactDetail, isLoading, isError, error } =
    useContactDetail(contactId);
  const { updateContact, isUpdatingContact } = useContactMutations(
    contactId,
    userUuid,
  );

  const [memoValue, setMemoValue] = useState('');
  const [isMarkAsUnread, setIsMarkAsUnread] = useState(false);
  const [originalMemo, setOriginalMemo] = useState('');
  const [originalStatus, setOriginalStatus] = useState('');
  const [hasAutoMarkedAsRead, setHasAutoMarkedAsRead] = useState(false);

  // Contact 데이터 로드 시 초기화 및 자동 READ 처리 (dependency 제거)
  useEffect(() => {
    if (contactDetail) {
      setMemoValue(contactDetail.memo || '');
      setOriginalMemo(contactDetail.memo || '');
      setOriginalStatus(contactDetail.status);
      setIsMarkAsUnread(false);

      // UNREAD 상태인 경우 자동으로 READ로 변경 (한 번만)
      if (
        contactDetail.status.toUpperCase() === 'UNREAD' &&
        !hasAutoMarkedAsRead
      ) {
        setHasAutoMarkedAsRead(true);
        updateContact({ status: 'READ' });
      }
    }
  }, [contactDetail]); // updateContact 의존성 제거

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'READ':
        return 'bg-[#7676E9]';
      case 'UNREAD':
        return 'bg-[#BBBBBB]';
      default:
        return 'bg-[#888]';
    }
  };

  const handleSave = () => {
    if (!contactDetail) return;

    const updateData: { memo?: string; status?: 'READ' | 'UNREAD' } = {};

    const memoChanged = memoValue !== originalMemo;
    const statusChanged = isMarkAsUnread;

    if (memoChanged) {
      updateData.memo = memoValue;
    }

    if (statusChanged) {
      updateData.status = 'UNREAD';
    }

    if (memoChanged || statusChanged) {
      updateContact(updateData);
      onClose();
    } else {
      onClose();
    }
  };

  if (isError) {
    alert(`Cannot load contact details: ${error?.message || 'Unknown error'}`);
    onClose();
    return null;
  }

  // 현재 표시할 상태 결정 (자동 READ 처리된 경우 READ로 표시)
  const currentStatus =
    hasAutoMarkedAsRead && contactDetail?.status.toUpperCase() === 'UNREAD'
      ? 'READ'
      : contactDetail?.status || '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-detail-modal-title"
    >
      <div className="max-h-[80vh] w-[40rem] overflow-y-auto rounded-lg bg-white">
        <div className="sticky top-0 flex w-full items-center justify-between border-b border-[#eaeaea] bg-white p-6">
          <h3 className="text-xl font-medium" id="contact-detail-modal-title">
            Contact Detail
          </h3>
          <button
            type="button"
            className="cursor-pointer bg-transparent"
            onClick={onClose}
            aria-label="Close contact detail modal"
            disabled={isUpdatingContact}
          >
            <X size={20} className="text-[#707070]" aria-hidden="true" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <Loader size={32} className="animate-spin text-[#3e3e3e]" />
          </div>
        ) : contactDetail ? (
          <div className="bg-[#F1F1F1] p-6">
            {isUpdatingContact && (
              <div className="mb-4 flex items-center justify-center rounded bg-[#f9f9f9] p-4">
                <Loader
                  size={16}
                  className="mr-2 animate-spin text-[#3e3e3e]"
                />
                <span className="text-sm text-[#666]">Updating contact...</span>
              </div>
            )}

            {/* Status and Date */}
            <div className="mb-6 flex items-center justify-between">
              <div className="text-sm text-[#666]">
                {format(new Date(contactDetail.createdAt), 'yyyy.MM.dd HH:mm')}
              </div>
              <span
                className={`inline-block rounded-full px-4 py-1 text-center text-xs font-semibold tracking-wide text-white ${getStatusColor(currentStatus)}`}
              >
                {currentStatus.toUpperCase()}
              </span>
            </div>

            {/* Contact Information */}
            <div className="mb-6 rounded border border-[#f1f1f1] bg-[#f9f9f9] p-4">
              <h4 className="mb-4 text-sm font-medium text-[#1d1d1d]">
                Contact Information
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-[#666]">Email:</span>
                  <div className="text-[#1d1d1d]">{contactDetail.email}</div>
                </div>
                <div>
                  <span className="font-medium text-[#666]">Company:</span>
                  <div className="text-[#1d1d1d]">
                    {contactDetail.companyName}
                  </div>
                </div>
                <div>
                  <span className="font-medium text-[#666]">
                    Representative:
                  </span>
                  <div className="text-[#1d1d1d]">
                    {contactDetail.representativeName}
                  </div>
                </div>
                <div>
                  <span className="font-medium text-[#666]">Phone:</span>
                  <div className="text-[#1d1d1d]">
                    {contactDetail.phoneNumber}
                  </div>
                </div>
                <div className="col-span-2">
                  <span className="font-medium text-[#666]">
                    Business Registration Number:
                  </span>
                  <div className="text-[#1d1d1d]">
                    {contactDetail.businessRegistrationNumber}
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="mb-6">
              <h4 className="mb-3 text-sm font-medium text-[#1d1d1d]">
                Content
              </h4>
              <div className="rounded border border-[#eaeaea] bg-white p-4 text-sm leading-relaxed text-[#1d1d1d]">
                {contactDetail.content}
              </div>
            </div>

            {/* Memo */}
            <div className="mb-6">
              <h4 className="mb-3 text-sm font-medium text-[#1d1d1d]">Memo</h4>
              <textarea
                className="w-full rounded border border-[#eaeaea] bg-white p-4 text-sm leading-relaxed text-[#666] outline-none focus:border-[#3e3e3e] disabled:opacity-50"
                value={memoValue}
                onChange={(e) => setMemoValue(e.target.value)}
                rows={4}
                placeholder="Enter memo..."
                disabled={isUpdatingContact}
              />
            </div>

            {/* Status */}
            <div className="mb-6">
              <h4 className="mb-3 text-sm font-medium text-[#1d1d1d]">
                Status
              </h4>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border border-[#eaeaea] bg-white text-[#3e3e3e] outline-none focus:ring-2 focus:ring-[#3e3e3e] focus:ring-opacity-25 disabled:opacity-50"
                  checked={isMarkAsUnread}
                  onChange={(e) => setIsMarkAsUnread(e.target.checked)}
                  disabled={isUpdatingContact}
                />
                <span className="ml-2 text-sm text-[#666]">Mark as UNREAD</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center p-12">
            <p className="text-lg text-[#666]">Contact not found</p>
          </div>
        )}

        <div className="sticky bottom-0 border-t border-[#eaeaea] bg-[#F1F1F1] p-4 text-right">
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="cursor-pointer rounded bg-[#666] px-4 py-2 text-sm font-medium text-white hover:bg-[#444] disabled:cursor-not-allowed disabled:opacity-50"
              onClick={onClose}
              aria-label="Cancel"
              disabled={isUpdatingContact}
            >
              Cancel
            </button>
            <button
              type="button"
              className="cursor-pointer rounded bg-[#3e3e3e] px-4 py-2 text-sm font-medium text-white hover:bg-[#222] disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleSave}
              aria-label="Save contact"
              disabled={isUpdatingContact}
            >
              {isUpdatingContact ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactDetailModal;
